import { CURVE_COUNT, PEBBLE_COUNT, type AuroraField } from './aurora-field'

type Rgb = [number, number, number]

interface AuroraPalette {
  base: Rgb
  mid: Rgb
  glow: Rgb
}

interface AuroraPaintOptions {
  field: AuroraField
  palette: AuroraPalette
}

interface AuroraUniforms {
  resolution: WebGLUniformLocation
  seed: WebGLUniformLocation
  warp: WebGLUniformLocation
  colors: WebGLUniformLocation
  pebbles: WebGLUniformLocation
  pebbleMeta: WebGLUniformLocation
  curveEnds: WebGLUniformLocation
  curveShape: WebGLUniformLocation
}

interface AuroraRenderer {
  canvas: HTMLCanvasElement
  gl: WebGL2RenderingContext
  program: WebGLProgram
  vao: WebGLVertexArrayObject
  uniforms: AuroraUniforms
}

const VERTEX_SHADER = `#version 300 es
out vec2 v_uv;
void main() {
  vec2 positions[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  vec2 position = positions[gl_VertexID];
  v_uv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 out_color;
uniform vec2 u_resolution;
uniform float u_seed;
uniform float u_warp;
uniform vec3 u_colors[3];
uniform vec4 u_pebbles[${PEBBLE_COUNT}];
uniform vec2 u_pebble_meta[${PEBBLE_COUNT}];
uniform vec4 u_curve_ends[${CURVE_COUNT}];
uniform vec4 u_curve_shape[${CURVE_COUNT}];

float hash(vec2 point) {
  return fract(sin(dot(point, vec2(127.1, 311.7)) + u_seed) * 43758.5453123);
}

float noise(vec2 point) {
  vec2 cell = floor(point);
  vec2 fraction = fract(point);
  fraction = fraction * fraction * (3.0 - 2.0 * fraction);
  return mix(mix(hash(cell), hash(cell + vec2(1.0, 0.0)), fraction.x),
    mix(hash(cell + vec2(0.0, 1.0)), hash(cell + vec2(1.0)), fraction.x), fraction.y);
}

vec3 to_linear(vec3 color) {
  return mix(color / 12.92, pow((color + 0.055) / 1.055, vec3(2.4)), step(0.04045, color));
}

vec3 to_srgb(vec3 color) {
  return mix(color * 12.92, 1.055 * pow(max(color, 0.0), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, color));
}

void main() {
  float aspect = u_resolution.x / u_resolution.y;
  vec2 warp = vec2(noise(v_uv * 1.7), noise(v_uv * 1.7 + vec2(5.2, 1.3))) - 0.5;
  vec2 uv = v_uv + warp * u_warp;
  float dark_field = 0.0;
  float light_field = 0.0;

  for (int index = 0; index < ${PEBBLE_COUNT}; index += 1) {
    vec4 pebble = u_pebbles[index];
    vec2 meta = u_pebble_meta[index];
    vec2 local = uv - pebble.xy;
    float cosine = cos(meta.x);
    float sine = sin(meta.x);
    local = mat2(cosine, -sine, sine, cosine) * local;
    vec2 radius = vec2(pebble.z, pebble.w);
    float distance_value = length(local / radius) - 1.0;
    dark_field += (1.0 - smoothstep(-0.42, 0.42, distance_value)) * meta.y;
  }

  vec2 point = vec2(uv.x * aspect, uv.y);
  for (int index = 0; index < ${CURVE_COUNT}; index += 1) {
    vec4 ends = u_curve_ends[index];
    vec4 shape = u_curve_shape[index];
    vec2 start = vec2(ends.x * aspect, ends.y);
    vec2 end = vec2(ends.z * aspect, ends.w);
    vec2 center = vec2(shape.x * aspect, shape.y);
    vec2 direction = normalize(end - start);
    vec2 normal = vec2(-direction.y, direction.x);
    vec2 local = point - center;
    float along = dot(local, direction);
    float across = dot(local, normal);
    across += sin(along / shape.z * 1.25 + u_seed * 6.283) * shape.z * 0.16;
    vec2 normalized = vec2(along / (shape.z * 1.45), across / (shape.z * 0.9));
    float field = exp(-dot(normalized, normalized) * 1.35) * shape.w;
    light_field = max(light_field, field);
  }

  vec2 right_delta = vec2((uv.x - 1.08) * 0.82, (uv.y - 0.48) * 1.24);
  float right_glow = exp(-dot(right_delta, right_delta) * 2.2) * 0.62;
  float variation = (noise(uv * 2.1 + 7.3) - 0.5) * 0.045;
  float level = clamp(0.44 - dark_field * 0.38 + light_field * 1.15 + right_glow + variation, 0.0, 1.0);

  vec3 base = to_linear(u_colors[0]);
  vec3 mid = to_linear(u_colors[1]);
  vec3 glow = to_linear(u_colors[2]);
  vec3 color = mix(base, mid, smoothstep(0.12, 0.72, level));
  color = mix(color, glow, smoothstep(0.56, 1.0, level) * 0.72);
  color *= 1.0 - clamp(dark_field * 0.13, 0.0, 0.22);

  vec2 centered = vec2((v_uv.x - 0.5) * aspect, v_uv.y - 0.5);
  color *= 1.0 - smoothstep(0.38, 1.12, length(centered)) * 0.14;
  out_color = vec4(to_srgb(color), 1.0);
}`

let sharedRenderer: AuroraRenderer | null | undefined

function compile(gl: WebGL2RenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('无法创建 Aurora shader')
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? 'Aurora shader 编译失败'
    gl.deleteShader(shader)
    throw new Error(message)
  }
  return shader
}

function uniform(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
) {
  const location = gl.getUniformLocation(program, name)
  if (!location) throw new Error(`缺少 Aurora uniform: ${name}`)
  return location
}

function createRenderer(): AuroraRenderer | null {
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    powerPreference: 'low-power',
    preserveDrawingBuffer: true,
  })
  if (!gl) return null
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
  const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
  const program = gl.createProgram()
  const vao = gl.createVertexArray()
  if (!program || !vao) return null
  gl.attachShader(program, vertex)
  gl.attachShader(program, fragment)
  gl.linkProgram(program)
  gl.deleteShader(vertex)
  gl.deleteShader(fragment)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) ?? 'Aurora program 链接失败')
  }
  return {
    canvas,
    gl,
    program,
    vao,
    uniforms: {
      resolution: uniform(gl, program, 'u_resolution'),
      seed: uniform(gl, program, 'u_seed'),
      warp: uniform(gl, program, 'u_warp'),
      colors: uniform(gl, program, 'u_colors[0]'),
      pebbles: uniform(gl, program, 'u_pebbles[0]'),
      pebbleMeta: uniform(gl, program, 'u_pebble_meta[0]'),
      curveEnds: uniform(gl, program, 'u_curve_ends[0]'),
      curveShape: uniform(gl, program, 'u_curve_shape[0]'),
    },
  }
}

function renderer() {
  if (sharedRenderer === undefined) {
    try {
      sharedRenderer = createRenderer()
    } catch (error) {
      console.warn('Aurora WebGL 初始化失败，使用纯色背景', error)
      sharedRenderer = null
    }
  }
  return sharedRenderer
}

function flattenField(field: AuroraField) {
  return {
    pebbles: new Float32Array(
      field.pebbles.flatMap((item) => [...item.center, ...item.radius]),
    ),
    pebbleMeta: new Float32Array(
      field.pebbles.flatMap((item) => [item.rotation, item.strength]),
    ),
    curveEnds: new Float32Array(
      field.curves.flatMap((item) => [...item.start, ...item.end]),
    ),
    curveShape: new Float32Array(
      field.curves.flatMap((item) => [
        ...item.control,
        item.width,
        item.strength,
      ]),
    ),
  }
}

function paintAurora(target: HTMLCanvasElement, options: AuroraPaintOptions) {
  const state = renderer()
  const context = target.getContext('2d')
  if (!state || !context) return false
  const { gl, program, uniforms } = state
  state.canvas.width = target.width
  state.canvas.height = target.height
  const field = flattenField(options.field)
  const colors = new Float32Array([
    ...options.palette.base,
    ...options.palette.mid,
    ...options.palette.glow,
  ])
  gl.viewport(0, 0, target.width, target.height)
  gl.useProgram(program)
  gl.bindVertexArray(state.vao)
  gl.uniform2f(uniforms.resolution, target.width, target.height)
  gl.uniform1f(uniforms.seed, options.field.seed / 4294967296)
  gl.uniform1f(uniforms.warp, options.field.warp)
  gl.uniform3fv(uniforms.colors, colors)
  gl.uniform4fv(uniforms.pebbles, field.pebbles)
  gl.uniform2fv(uniforms.pebbleMeta, field.pebbleMeta)
  gl.uniform4fv(uniforms.curveEnds, field.curveEnds)
  gl.uniform4fv(uniforms.curveShape, field.curveShape)
  gl.drawArrays(gl.TRIANGLES, 0, 3)
  context.clearRect(0, 0, target.width, target.height)
  context.drawImage(state.canvas, 0, 0)
  return true
}

let colorCanvas: HTMLCanvasElement | undefined

function readColor(value: string, fallback: Rgb): Rgb {
  colorCanvas ??= document.createElement('canvas')
  colorCanvas.width = 1
  colorCanvas.height = 1
  const context = colorCanvas.getContext('2d', { willReadFrequently: true })
  if (!context) return fallback
  context.clearRect(0, 0, 1, 1)
  context.fillStyle = value.trim()
  context.fillRect(0, 0, 1, 1)
  const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data
  if (alpha === 0) return fallback
  return [red / 255, green / 255, blue / 255]
}

function readAuroraPalette(element: Element): AuroraPalette {
  const style = getComputedStyle(element)
  const base =
    style.getPropertyValue('--aurora-base') ||
    style.getPropertyValue('--highlight-base')
  const mid =
    style.getPropertyValue('--aurora-mid') ||
    style.getPropertyValue('--highlight-mid')
  const glow =
    style.getPropertyValue('--aurora-glow') ||
    style.getPropertyValue('--highlight-glow')
  return {
    base: readColor(base, [0.035, 0.22, 0.17]),
    mid: readColor(mid, [0.07, 0.4, 0.3]),
    glow: readColor(glow, [0.18, 0.62, 0.46]),
  }
}

export { paintAurora, readAuroraPalette }
